/**
 * `/$tenantSlug/concierge` — full-page Concierge placeholder.
 *
 * Phase J: replaced the prior static-mock page. The earlier version
 * rendered hardcoded sample messages + a non-functional input that
 * shared testids with the floating dock — anyone landing here saw
 * fake data and a broken submit. That is a worse experience than
 * sending them to the live dock.
 *
 * For now this route is a simple "open the dock" prompt. A real
 * full-page Concierge surface (when we add one) wires through the
 * same runtime adapter as the dock so behavior stays consistent.
 */

import { MessageCircle } from "lucide-react";
import { useEffect, type ReactElement } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { Button, Card, CardContent } from "@ken/ui";

export const Route = createFileRoute("/$tenantSlug/concierge")({
  component: ConciergeRoute,
});

function openDock(): void {
  // Synthesise the Cmd+K event the dock listens for. Lets the user
  // open the dock without leaving this route.
  window.dispatchEvent(
    new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true }),
  );
}

function ConciergeRoute(): ReactElement {
  // Auto-open the dock on first mount so the page feels responsive.
  useEffect(() => {
    openDock();
  }, []);

  return (
    <div
      aria-label="Concierge"
      data-testid="concierge-page"
      className="flex flex-col items-center justify-center min-h-[60vh] p-8"
    >
      <Card className="max-w-md text-center">
        <CardContent className="p-8 flex flex-col items-center gap-4">
          <div className="grid place-items-center w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-brand)] to-[oklch(28%_0.08_230)] text-white">
            <MessageCircle size={28} />
          </div>
          <h1 className="text-xl font-semibold">Concierge</h1>
          <p className="text-sm text-[var(--color-text-soft)]">
            The Concierge lives in the floating dock in the bottom-right
            corner — it&apos;s available on every page. Press{" "}
            <kbd className="px-1.5 py-0.5 rounded border border-[oklch(0%_0_0_/_0.30)] text-xs font-mono">
              ⌘K
            </kbd>{" "}
            to open it from anywhere.
          </p>
          <Button
            variant="primary"
            onClick={openDock}
            data-testid="concierge-page-open-dock"
          >
            Open Concierge
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
